/**
 * pages/ReviewPage.jsx
 * Public /review/{token} page.
 * Flow:
 *  1. Validate token (public API call)
 *  2. If invalid/expired → show error
 *  3. If already reviewed → show "already done" message
 *  4. If not logged in → show inline login/register prompt
 *  5. If logged in as uploader → "Reviewer only" error
 *  6. If logged in as reviewer → show full read-only report + approve/reject buttons
 */

import React, { useEffect, useState, useCallback } from 'react';
import API from '../services/api';
import AuthPage from './AuthPage';
import { fmtTolPlus, fmtTolMinus } from '../utils/tolerance';

const CC_SYMBOL_LABELS = {
  // Current symbols
  spade:               'Spade (♠)',
  club:                'Club (♣)',
  non_filled_square:   'Non-Filled Square (□)',
  filled_square:       'Filled Square (■)',
  non_filled_circle:   'Non-Filled Circle (○)',
  circle_filled:       'Filled Circle (●)',
  non_filled_triangle: 'Non-Filled Triangle (△)',
  filled_triangle:     'Filled Triangle (▲)',
  non_filled_diamond:  'Non-Filled Diamond (◇)',
  filled_diamond:      'Filled Diamond (◆)',
  oblong:              'Oblong (⬭)',
  // Legacy keys for older DB records
  circle_dot:          'Circular Dot (◉)',
  diamond:             'Filled Diamond (◆)',
};

const CAT_LABELS = {
  linear_dimensions:          'Linear Dimensions',
  angular_dimensions:         'Angular Dimensions',
  circular_radial_dimensions: 'Circular / Radial',
  chamfer_bevel_dimensions:   'Chamfer & Bevel',
  coordinate_dimensions:      'Coordinate Dims',
  thread_dimensions:          'Thread Dimensions',
  surface_dimensions:         'Surface Dims',
  taper_slope_dimensions:     'Taper & Slope',
  gdt_feature_control:        'GD&T',
  surface_finish_cosmetic:    'Surface Finish',
  material_metallurgy:        'Material & Metallurgy',
  manufacturing_process:      'Manufacturing Process',
  assembly_fit:               'Assembly & Fit',
  inspection_quality:         'Inspection & Quality',
  regulatory_compliance:      'Regulatory Compliance',
  environmental_durability:   'Environmental',
};

/* ── UTC parse helper — backend sends naive UTC strings without 'Z' ── */
const toUTC = iso => iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z') : null;

/* ── Helpers ─────────────────────────────────────────────────── */
function fmtDate(iso) {
  if (!iso) return '—';
  return toUTC(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Append ?token=<shareToken> to an image URL so the (now-protected) /api/image
 * endpoints accept the reviewer's capability token instead of requiring a JWT.
 */
function withToken(url, shareToken) {
  if (!url || !shareToken) return url;
  return `${url}?token=${encodeURIComponent(shareToken)}`;
}

function MultiPageAnnotatedViewer({ urls, shareToken, drawingStyle }) {
  const [page, setPage] = React.useState(0);
  if (!urls || urls.length === 0) return null;
  const isMulti = urls.length > 1;
  return (
    <div>
      {isMulti && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {urls.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setPage(idx)}
              style={{
                padding: '3px 12px',
                borderRadius: 5,
                border: page === idx ? '2px solid #2563eb' : '1px solid #d1d5db',
                background: page === idx ? '#eff6ff' : '#f9fafb',
                color: page === idx ? '#1d4ed8' : '#374151',
                fontWeight: page === idx ? 600 : 400,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Page {idx + 1}
            </button>
          ))}
        </div>
      )}
      <img
        key={urls[page]}
        src={withToken(urls[page], shareToken)}
        alt={`Drawing page${isMulti ? ` ${page + 1}` : ''}`}
        style={drawingStyle}
      />
      {isMulti && (
        <p style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
          Page {page + 1} of {urls.length}
        </p>
      )}
    </div>
  );
}

function normalizeCC(v) {
  if (!v) return 'No';
  return String(v).trim().toLowerCase() === 'yes' ? 'Yes' : 'No';
}

/* ── Mini action modal ───────────────────────────────────────── */
function ActionModal({ type, onConfirm, onCancel, loading }) {
  const [comment, setComment] = useState('');
  const isReject = type === 'reject';

  return (
    <div style={s.overlay}>
      <div style={s.miniModal}>
        <h3 style={s.miniTitle}>
          {isReject ? 'Reject Inspection Plan' : 'Approve Inspection Plan'}
        </h3>
        <p style={s.miniDesc}>
          {isReject
            ? 'Please provide a reason for rejection. A comment is required.'
            : 'Optionally add a comment before approving.'}
        </p>
        <textarea
          style={{ ...s.textarea, ...(isReject && !comment.trim() ? {} : {}) }}
          placeholder={isReject ? 'Rejection reason (required)…' : 'Comment (optional)…'}
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={4}
        />
        <div style={s.miniActions}>
          <button style={s.cancelBtn} onClick={onCancel} disabled={loading}>Cancel</button>
          <button
            style={{ ...s.confirmBtn, ...(isReject ? s.confirmReject : s.confirmApprove), ...(loading ? s.btnDisabled : {}) }}
            onClick={() => onConfirm(comment)}
            disabled={loading || (isReject && !comment.trim())}
          >
            {loading ? <span style={s.spinner} /> : (isReject ? 'Reject' : 'Approve')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── 4-column Notes table ────────────────────────────────────── */
function NotesTable({ items = [] }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ ...s.card, marginTop: 16 }}>
      <h2 style={s.sectionTitle}>Notes &amp; Specifications
        <span style={s.planCount}>{items.length} note{items.length !== 1 ? 's' : ''}</span>
      </h2>
      <div style={s.tableWrap}>
        <table style={s.planTable}>
          <thead>
            <tr>
              {['Category', 'Note / Specification', 'Value / Detail', 'Source on Drawing'].map(h => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((n, i) => (
              <tr key={i} style={s.planRow}>
                <td style={{ ...s.td, whiteSpace: 'nowrap', fontSize: 11, color: '#6B6360' }}>{n.category || '—'}</td>
                <td style={{ ...s.td, fontWeight: 500 }}>{n.note_label || '—'}</td>
                <td style={{ ...s.td, maxWidth: 260, wordBreak: 'break-word' }}>{n.value_detail || '—'}</td>
                <td style={{ ...s.td, fontSize: 11, color: '#9C9490' }}>{n.source_on_drawing || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── GD&T table ──────────────────────────────────────────────── */
function GdtSection({ rows = [] }) {
  if (!rows || rows.length === 0) return null;
  const cols = Object.keys(rows[0] || {}).filter(k => !['id', 'item_no'].includes(k));
  if (cols.length === 0) return null;
  return (
    <div style={{ ...s.card, marginTop: 16 }}>
      <h2 style={s.sectionTitle}>GD&amp;T Table
        <span style={s.planCount}>{rows.length} row{rows.length !== 1 ? 's' : ''}</span>
      </h2>
      <div style={s.tableWrap}>
        <table style={s.planTable}>
          <thead>
            <tr>
              {cols.map(c => <th key={c} style={s.th}>{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={s.planRow}>
                {cols.map(c => <td key={c} style={s.td}>{row[c] != null ? String(row[c]) : '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Low-Confidence table ─────────────────────────────────────── */
function LowConfidenceSection({ items = [] }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ ...s.card, marginTop: 16, border: '1px solid #FED7AA' }}>
      <h2 style={{ ...s.sectionTitle, color: '#B45309' }}>Low-Confidence Dimensions
        <span style={{ ...s.planCount, color: '#D97706' }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>
      </h2>
      <div style={s.tableWrap}>
        <table style={s.planTable}>
          <thead>
            <tr style={{ background: '#FFFBEB' }}>
              {['#', 'Location', 'Specification', 'Nominal', '+Tol', '-Tol', 'Reason'].map(h => (
                <th key={h} style={{ ...s.th, background: '#FFFBEB', color: '#B45309' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} style={s.planRow}>
                <td style={{ ...s.td, color: '#9C9490' }}>{item.item_no || idx + 1}</td>
                <td style={s.td}>{item.location_on_drawing || '—'}</td>
                <td style={s.td}>{item.specification || '—'}</td>
                <td style={s.tdNum}>{item.nominal ?? '—'}</td>
                <td style={s.tdNum}>{fmtTolPlus(item.tolerance_plus)}</td>
                <td style={s.tdNum}>{fmtTolMinus(item.tolerance_minus)}</td>
                <td style={{ ...s.td, fontSize: 11, color: '#B45309' }}>{item.exclusion_reason_label || item.exclusion_reason || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Per-part plan sections (used by tabular) ─────────────────── */
function PartPlanSections({ fp = {} }) {
  const items   = fp.inspection_rows   || fp.inspection_items  || [];
  const lcItems = fp.low_confidence_rows || fp.low_confidence_dimensions || [];
  const gdtRows = fp.gdt_rows          || fp.gdt_table_rows    || [];
  const notes   = fp.notes_rows        || fp.notes_items       || [];
  return (
    <>
      <div style={{ ...s.card, marginTop: 16 }}>
        <h2 style={s.sectionTitle}>Inspection Plan
          <span style={s.planCount}>{items.length} items</span>
        </h2>
        {items.length === 0
          ? <p style={{ color: '#9C9490', fontSize: '0.85rem', margin: 0 }}>No items.</p>
          : (
            <div style={s.tableWrap}>
              <table style={s.planTable}>
                <thead>
                  <tr>
                    {['#', 'Location', 'CC', 'Specification', 'Nominal', '+Tol', '-Tol', 'UCL', 'LCL', 'Type', 'Tool', 'GD&T'].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => (
                    <tr key={idx} style={s.planRow}>
                      <td style={{ ...s.td, color: '#9C9490' }}>{item.item_no}</td>
                      <td style={s.td}>{item.location_on_drawing || '—'}</td>
                      <td style={s.td}>
                        {normalizeCC(item.critical_characteristic) === 'Yes'
                          ? <span style={s.ccYes}>CC</span>
                          : <span style={s.ccNo}>—</span>}
                      </td>
                      <td style={s.td}>{item.specification || '—'}</td>
                      <td style={s.tdNum}>{item.nominal ?? '—'}</td>
                      <td style={s.tdNum}>{fmtTolPlus(item.tolerance_plus)}</td>
                      <td style={s.tdNum}>{fmtTolMinus(item.tolerance_minus)}</td>
                      <td style={s.tdNum}>{item.ucl ?? '—'}</td>
                      <td style={s.tdNum}>{item.lcl ?? '—'}</td>
                      <td style={s.td}>{CAT_LABELS[item.spec_category] || item.inspection_type || '—'}</td>
                      <td style={s.td}>{item.inspection_tool || '—'}</td>
                      <td style={{ ...s.td, fontSize: 11 }}>{item.gdt?.trim() ? item.gdt : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
      <GdtSection rows={gdtRows} />
      {/* Low-confidence rows intentionally hidden on the reviewer page — reviewers approve the finished plan only */}
      <NotesTable items={notes} />
    </>
  );
}

/* ── Tabular parts accordion (reviewer) ──────────────────────── */
function TabularPartsAccordion({ variants = [] }) {
  const [openIdx, setOpenIdx] = React.useState(0);
  if (!variants.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
      {variants.map((v, i) => {
        const isOpen = openIdx === i;
        const partNo = v.part_no || `Part ${i + 1}`;
        return (
          <div key={i} style={{ border: isOpen ? '1.5px solid #F97316' : '1.5px solid #E5E0DA', borderRadius: 10, overflow: 'hidden' }}>
            <div
              onClick={() => setOpenIdx(isOpen ? -1 : i)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', background: isOpen ? '#FFF7ED' : '#FAFAF8', cursor: 'pointer', userSelect: 'none' }}
            >
              <span style={{ fontSize: 11, color: '#B45309', fontWeight: 700 }}>{isOpen ? '▼' : '▶'}</span>
              <span style={{ fontWeight: 600, fontSize: 14, color: '#1A1410' }}>{partNo}</span>
              <span style={{ fontSize: 11, color: '#9C9490' }}>
                {(v.final_plan?.inspection_rows || v.final_plan?.inspection_items || []).length} items
              </span>
            </div>
            {isOpen && (
              <div style={{ padding: '0 16px 16px' }}>
                <PartPlanSections fp={v.final_plan || {}} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Shared inspection table ─────────────────────────────────── */
function InspectionTable({ items = [], ccSymbol }) {
  if (items.length === 0) {
    return <p style={{ color: '#9C9490', fontSize: '0.85rem', margin: 0 }}>No inspection items found.</p>;
  }
  return (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {['#', 'Location', 'CC', 'Specification', 'Nominal', '+Tol', '-Tol', 'UCL', 'LCL', 'Type', 'Tool', 'Freq', 'GD&T'].map(h => (
              <th key={h} style={{ padding: '6px 8px', background: '#F8F6F3', borderBottom: '1.5px solid #E5E0DA',
                fontWeight: 600, fontSize: 11, textAlign: 'left', color: '#6B6360', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => (
            <tr key={idx} style={{ borderBottom: '1px solid #F0EDE8', background: idx % 2 === 0 ? '#FAFAF8' : '#fff' }}>
              <td style={{ padding: '5px 8px', color: '#9C9490', fontSize: 12 }}>{item.item_no}</td>
              <td style={{ padding: '5px 8px' }}>{item.location_on_drawing || '—'}</td>
              <td style={{ padding: '5px 8px' }}>
                {normalizeCC(item.critical_characteristic) === 'Yes'
                  ? <span style={{ background: '#FEF2F2', color: '#991B1B', padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700 }}>CC</span>
                  : <span style={{ color: '#BDB9B5' }}>—</span>}
              </td>
              <td style={{ padding: '5px 8px', maxWidth: 180, wordBreak: 'break-word' }}>{item.specification || '—'}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{item.nominal ?? '—'}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right', color: '#065F46' }}>{fmtTolPlus(item.tolerance_plus)}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right', color: '#991B1B' }}>{fmtTolMinus(item.tolerance_minus)}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{item.ucl ?? '—'}</td>
              <td style={{ padding: '5px 8px', textAlign: 'right' }}>{item.lcl ?? '—'}</td>
              <td style={{ padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap' }}>{CAT_LABELS[item.spec_category] || item.inspection_type || '—'}</td>
              <td style={{ padding: '5px 8px', fontSize: 11 }}>
                {item.inspection_tool || '—'}
                {item.tool_source === 'model' && (
                  <span title={item.tool_rationale || 'Selected by the model'} style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: '#dbeafe', color: '#1d4ed8', marginLeft: 5, textTransform: 'uppercase', letterSpacing: .4, verticalAlign: 'middle' }}>AI</span>
                )}
              </td>
              <td style={{ padding: '5px 8px', fontSize: 11, whiteSpace: 'pre-wrap', maxWidth: 220 }}>{item.gdt?.trim() ? item.gdt : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Batch file tab strip ─────────────────────────────────────── */
function BatchFileStrip({ files, focusedId, fileDecisions, onSelect }) {
  return (
    <div style={{
      display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16,
      padding: '10px 16px', background: '#FFFBF5',
      border: '1px solid #FED7AA', borderRadius: 8,
    }}>
      {files.map((f, i) => {
        const dec = fileDecisions?.[String(f.drawing_id)]?.status || 'pending';
        const isActive = f.drawing_id === focusedId;
        const decColor = dec === 'approved' ? '#065F46' : dec === 'rejected' ? '#991B1B' : '#6B6360';
        const decBg    = dec === 'approved' ? '#ECFDF5' : dec === 'rejected' ? '#FEF2F2' : '#F2EDE8';
        return (
          <button key={f.drawing_id} onClick={() => onSelect(f.drawing_id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
              border: isActive ? '2px solid #F97316' : '1.5px solid #E5E0DA',
              background: isActive ? '#FFF7ED' : '#FAFAF9',
              fontWeight: isActive ? 600 : 400,
            }}>
            <span style={{ color: '#9C9490', fontSize: 11 }}>#{i + 1}</span>
            <span style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {f.file_name}
            </span>
            <span style={{ padding: '1px 7px', borderRadius: 12, fontSize: 10, fontWeight: 700, background: decBg, color: decColor }}>
              {dec === 'pending' ? '—' : dec}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Main component ───────────────────────────────────────────── */
export default function ReviewPage({ token, authUser, onAuthSuccess, onBackToDashboard }) {
  const [loading,     setLoading]     = useState(true);
  const [reportData,  setReportData]  = useState(null);
  const [tokenError,  setTokenError]  = useState(null);  // 'invalid' | 'expired' | 'reviewed'
  const [expiredView, setExpiredView] = useState(false); // expired link: view-only
  const [actionModal, setActionModal] = useState(null);  // 'approve' | 'reject'
  const [actionLoading, setActionLoading] = useState(false);
  const [actionDone,  setActionDone]  = useState(null);  // success message after action
  const [actionError, setActionError] = useState(null);
  // Batch-specific state
  const [expandedFileIds,    setExpandedFileIds]    = useState(new Set());
  const [batchFileDecisions, setBatchFileDecisions] = useState({});
  const [batchActionModal,  setBatchActionModal]  = useState(null); // { type:'approve'|'reject', drawingId }
  const [batchAllDone,      setBatchAllDone]      = useState(false);

  const userRole = authUser
    ? (localStorage.getItem('auth_role') || 'uploader')
    : null;

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setTokenError(null);
    try {
      const data = await API.getReviewToken(token);
      if (!data.token_valid) {
        setTokenError('invalid');
        return;
      }
      if (data.token_expired) {
        setExpiredView(true);   // read-only: report visible, decisions disabled
      }
      setReportData(data);
      // Initialise batch state
      if (data.is_batch && data.files?.length) {
        const decisions = data.file_decisions || {};
        setBatchFileDecisions(decisions);
        const allDecided = data.files.every(f => {
          const dec = decisions[String(f.drawing_id)]?.status;
          return dec === 'approved' || dec === 'rejected';
        });
        setBatchAllDone(allDecided);
        // Auto-expand the first undecided file (or first file if all decided)
        const firstUndecided = data.files.find(f => {
          const dec = decisions[String(f.drawing_id)]?.status;
          return dec !== 'approved' && dec !== 'rejected';
        }) || data.files[0];
        setExpandedFileIds(new Set([firstUndecided.drawing_id]));
      }
    } catch {
      setTokenError('invalid');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Single-file approve/reject
  const handleAction = async (comment) => {
    setActionError(null);
    setActionLoading(true);
    try {
      if (actionModal === 'approve') {
        await API.approveReview(token, comment);
        setActionDone('Inspection plan approved successfully. Redirecting to your dashboard…');
      } else {
        await API.rejectReview(token, comment);
        setActionDone('Inspection plan rejected. Redirecting to your dashboard…');
      }
      setActionModal(null);
      setTimeout(() => {
        if (onBackToDashboard) onBackToDashboard();
        else window.location.href = '/';
      }, 2000);
    } catch (err) {
      setActionError(err.error || err.message || 'Action failed. Please try again.');
      setActionModal(null);
    } finally {
      setActionLoading(false);
    }
  };

  // Batch per-file approve/reject
  const handleBatchAction = async (comment) => {
    if (!batchActionModal) return;
    const { type, drawingId } = batchActionModal;
    setActionError(null);
    setActionLoading(true);
    try {
      let res;
      if (type === 'approve') {
        res = await API.approveReview(token, comment, drawingId);
      } else {
        res = await API.rejectReview(token, comment, drawingId);
      }
      const newDecisions = res.file_decisions || { ...batchFileDecisions, [String(drawingId)]: { status: type === 'approve' ? 'approved' : 'rejected', comment } };
      setBatchFileDecisions(newDecisions);

      const allDecided = reportData.files.every(f => {
        const dec = newDecisions[String(f.drawing_id)]?.status;
        return dec === 'approved' || dec === 'rejected';
      });
      setBatchAllDone(allDecided);
      setBatchActionModal(null);

      if (allDecided) {
        setActionDone('All files reviewed! Redirecting to your dashboard…');
        setTimeout(() => {
          if (onBackToDashboard) onBackToDashboard();
          else window.location.href = '/';
        }, 2500);
      } else {
        // Auto-expand next undecided file
        const nextUndecided = reportData.files.find(f => {
          const dec = newDecisions[String(f.drawing_id)]?.status;
          return dec !== 'approved' && dec !== 'rejected';
        });
        if (nextUndecided) {
          setExpandedFileIds(prev => {
            const next = new Set(prev);
            next.add(nextUndecided.drawing_id);
            return next;
          });
        }
      }
    } catch (err) {
      setActionError(err.error || err.message || 'Action failed. Please try again.');
      setBatchActionModal(null);
    } finally {
      setActionLoading(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div style={s.centerPage}>
        <div style={s.logoRow}>
          <div style={s.logoIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
            </svg>
          </div>
          <span style={s.logoText}>InSpectly.AI</span>
        </div>
        <p style={s.loadingText}>Loading inspection plan…</p>
      </div>
    );
  }

  /* ── Token invalid ── */
  if (tokenError === 'invalid') {
    return (
      <div style={s.centerPage}>
        <MessageCard type="error" title="Invalid Review Link" message="This review link is invalid or does not exist." />
      </div>
    );
  }

  /* ── Token expired ── */
  if (tokenError === 'expired') {
    return (
      <div style={s.centerPage}>
        <MessageCard type="warning" title="Link Expired" message="This review link has expired (links are valid for 7 days). Please ask the uploader to share a new link." />
      </div>
    );
  }

  /* ── Already reviewed — fall through to full report view below ── */

  /* ── Not logged in → prompt login ── */
  if (!authUser) {
    return (
      <div style={s.authGateWrap}>
        <div style={s.authGateBanner}>
          <strong>Reviewer login required</strong> — please sign in or register as a Reviewer to review this inspection plan.
        </div>
        <AuthPage
          onAuthSuccess={(data) => { onAuthSuccess(data); }}
          initialRole="reviewer"
        />
      </div>
    );
  }

  /* ── Wrong role ── */
  if (userRole !== 'reviewer') {
    return (
      <div style={s.centerPage}>
        <MessageCard
          type="warning"
          title="Reviewer Access Only"
          message="This link is intended for Reviewers. Your account is registered as an Uploader. Please log out and sign in with a Reviewer account."
        />
      </div>
    );
  }

  /* ── Batch review view ── */
  if (reportData?.is_batch) {
    const alreadyReviewed = reportData.already_reviewed === true;
    const totalFiles = reportData.files?.length || 0;
    const decidedCount = (reportData.files || []).filter(f => {
      const dec = batchFileDecisions[String(f.drawing_id)]?.status;
      return dec === 'approved' || dec === 'rejected';
    }).length;

    const toggleFile = (id) => {
      setExpandedFileIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    };

    return (
      <div style={s.page}>
        <header style={s.pageHeader}>
          <div style={s.logoRow}>
            <div style={s.logoIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
              </svg>
            </div>
            <span style={{ ...s.logoText, fontSize: '0.95rem' }}>InSpectly.AI</span>
          </div>
          <div style={s.headerRight}>
            {onBackToDashboard && (
              <button onClick={onBackToDashboard} style={s.backBtn}>← Dashboard</button>
            )}
            <span style={{ ...s.reviewerChip, background: '#FFF7ED', color: '#C2410C' }}>
              Batch Review — {totalFiles} files
            </span>
            {authUser && <span style={s.headerUser}>{authUser.username}</span>}
          </div>
        </header>

        <div style={s.content}>
          {actionDone  && <div style={s.successBanner}><span style={s.successCheck}>✓</span>{actionDone}</div>}
          {actionError && <div style={s.errorBanner}><span style={s.errorDot}>!</span>{actionError}</div>}

          {/* Batch summary header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: '#FFF7ED', border: '1px solid #FED7AA',
            borderRadius: 10, padding: '12px 18px', marginBottom: 16,
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#92400E' }}>
                Batch Inspection Review
              </div>
              <div style={{ fontSize: 12, color: '#B45309', marginTop: 3 }}>
                {decidedCount} of {totalFiles} files reviewed
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {batchAllDone && !actionDone && (
                <span style={{ padding: '4px 14px', borderRadius: 20, fontWeight: 700, fontSize: 12, background: '#ECFDF5', color: '#065F46' }}>
                  ✓ All Reviewed
                </span>
              )}
              {/* Progress dots */}
              <div style={{ display: 'flex', gap: 4 }}>
                {(reportData.files || []).map(f => {
                  const dec = batchFileDecisions[String(f.drawing_id)]?.status || 'pending';
                  const dotColor = dec === 'approved' ? '#16A34A' : dec === 'rejected' ? '#DC2626' : '#D4C8BE';
                  return <div key={f.drawing_id} title={f.file_name} style={{ width: 10, height: 10, borderRadius: '50%', background: dotColor }} />;
                })}
              </div>
            </div>
          </div>

          {/* All-reviewed completion banner */}
          {batchAllDone && !actionDone && (
            <div style={{ ...s.successBanner, marginBottom: 16 }}>
              <span style={s.successCheck}>✓</span>
              All {totalFiles} files reviewed!
              {onBackToDashboard && (
                <button onClick={onBackToDashboard} style={{ marginLeft: 16, ...s.backBtn, color: '#166534', border: '1px solid #86EFAC', background: '#dcfce7' }}>
                  ← Back to Dashboard
                </button>
              )}
            </div>
          )}

          {/* Accordion — one card per file */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(reportData.files || []).map((f, idx) => {
              const dec = batchFileDecisions[String(f.drawing_id)]?.status || 'pending';
              const decided  = dec === 'approved' || dec === 'rejected';
              const isOpen   = expandedFileIds.has(f.drawing_id);
              const meta         = f.metadata || {};
              const items        = f.inspection_plan_rows || [];
              const fIsTabular   = f.scan_type === 'tabular';
              const fVariants    = f.variants || [];
              const fLcItems     = f.low_confidence_dimensions || [];
              const fGdtRows     = f.gdt_table_rows || [];
              const fNotesItems  = f.notes_items || [];

              const decBadgeStyle = {
                padding: '3px 12px', borderRadius: 20, fontWeight: 700, fontSize: 11,
                flexShrink: 0,
                background: dec === 'approved' ? '#ECFDF5' : dec === 'rejected' ? '#FEF2F2' : '#F7F4F0',
                color:      dec === 'approved' ? '#065F46' : dec === 'rejected' ? '#991B1B' : '#9C9490',
              };

              return (
                <div key={f.drawing_id} style={{
                  border: isOpen ? '1.5px solid #F97316' : '1.5px solid #E5E0DA',
                  borderRadius: 10, overflow: 'hidden',
                  transition: 'border-color 0.15s',
                }}>
                  {/* Accordion header — always visible */}
                  <div
                    onClick={() => toggleFile(f.drawing_id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 18px',
                      background: isOpen ? '#FFF7ED' : '#FAFAF8',
                      cursor: 'pointer', userSelect: 'none',
                      transition: 'background 0.15s',
                    }}
                  >
                    {/* Expand toggle */}
                    <span style={{ fontSize: 11, color: '#B45309', fontWeight: 700, flexShrink: 0 }}>
                      {isOpen ? '▼' : '▶'}
                    </span>

                    {/* File number */}
                    <span style={{ color: '#9C9490', fontSize: 12, flexShrink: 0 }}>#{idx + 1}</span>

                    {/* File name */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: '#1A1410', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.file_name}
                      </div>
                      <div style={{ fontSize: 11, color: '#9C9490', marginTop: 2 }}>
                        {meta.part_no && <span>Part: {meta.part_no} · </span>}
                        {meta.drawing_no && <span>Dwg: {meta.drawing_no} · </span>}
                        {f.item_count ?? 0} items · {f.cc_count ?? 0} CCs
                      </div>
                    </div>

                    {/* Decision badge */}
                    <span style={decBadgeStyle}>
                      {dec === 'approved' ? '✓ Approved' : dec === 'rejected' ? '✕ Rejected' : 'Pending'}
                    </span>
                  </div>

                  {/* Accordion body — full content visible when open */}
                  {isOpen && (
                    <div style={{ padding: '0 18px 18px', background: '#FFFFFF' }}>

                      {/* Drawing information */}
                      <div style={{ ...s.card, margin: '14px 0 0' }}>
                        <h2 style={s.sectionTitle}>Drawing Information</h2>
                        <div style={s.metaGrid}>
                          {[
                            ['Part No.',    meta.part_no    || '—'],
                            ['Part Name',   meta.part_name  || '—'],
                            ['Drawing No.', meta.drawing_no || '—'],
                            ['Revision',    meta.rev_no     || '—'],
                            ['File Name',   f.file_name     || '—'],
                            ['CC Count',    f.cc_count      ?? '—'],
                            ['Item Count',  f.item_count    ?? '—'],
                            ['Shared By',   reportData?.uploader_name || '—'],
                          ].map(([label, value]) => (
                            <div key={label} style={s.metaItem}>
                              <span style={s.metaLabel}>{label}</span>
                              <span style={s.metaValue}>{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Original drawing — native PDF viewer (all pages scroll) */}
                      <div style={{ ...s.card, margin: '14px 0 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                          <h2 style={{ ...s.sectionTitle, marginBottom: 0 }}>Original Drawing</h2>
                          <a
                            href={withToken(`/api/image/${f.drawing_id}/pdf`, token) + '&download=1'}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '6px 14px', borderRadius: 6,
                              background: '#f0f9ff', border: '1px solid #bae6fd',
                              color: '#0369a1', fontWeight: 500, fontSize: 13,
                              textDecoration: 'none', whiteSpace: 'nowrap',
                            }}
                          >
                            ⬇ Download PDF
                          </a>
                        </div>
                        <iframe
                          title="Original drawing"
                          src={withToken(`/api/image/${f.drawing_id}/pdf`, token)}
                          style={{ width: '100%', height: '60vh', border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc' }}
                        />
                      </div>

                      {/* Tabular: all parts accordion */}
                      {fIsTabular && fVariants.length > 0 && (
                        <div style={{ ...s.card, margin: '14px 0 0' }}>
                          <h2 style={s.sectionTitle}>
                            Inspection Plans — {fVariants.length} Part{fVariants.length !== 1 ? 's' : ''}
                          </h2>
                          <TabularPartsAccordion variants={fVariants} />
                        </div>
                      )}

                      {/* Normal scan: Main plan + GD&T + LC + Notes */}
                      {!fIsTabular && (
                        <>
                          <div style={{ ...s.card, margin: '14px 0 0' }}>
                            <h2 style={s.sectionTitle}>
                              Inspection Plan
                              <span style={s.planCount}>{items.length} items</span>
                            </h2>
                            {items.length === 0 ? (
                              <p style={{ color: '#9C9490', fontSize: '0.85rem', margin: 0 }}>No inspection items found.</p>
                            ) : (
                              <div style={s.tableWrap}>
                                <table style={s.planTable}>
                                  <thead>
                                    <tr>
                                      {['#', 'Location', 'CC', 'Specification', 'Nominal', '+Tol', '-Tol', 'UCL', 'LCL', 'Type / Classification', 'Tool', 'GD&T'].map(h => (
                                        <th key={h} style={s.th}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((item, rowIdx) => (
                                      <tr key={rowIdx} style={s.planRow}>
                                        <td style={{ ...s.td, color: '#9C9490' }}>{item.item_no}</td>
                                        <td style={s.td}>{item.location_on_drawing || '—'}</td>
                                        <td style={s.td}>
                                          {normalizeCC(item.critical_characteristic) === 'Yes'
                                            ? <span style={s.ccYes}>CC</span>
                                            : <span style={s.ccNo}>—</span>}
                                        </td>
                                        <td style={s.td}>{item.specification || '—'}</td>
                                        <td style={s.tdNum}>{item.nominal ?? '—'}</td>
                                        <td style={s.tdNum}>{fmtTolPlus(item.tolerance_plus)}</td>
                                        <td style={s.tdNum}>{fmtTolMinus(item.tolerance_minus)}</td>
                                        <td style={s.tdNum}>{item.ucl ?? '—'}</td>
                                        <td style={s.tdNum}>{item.lcl ?? '—'}</td>
                                        <td style={{ ...s.td, minWidth: 130 }}>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            <span style={{
                                              display: 'inline-block', fontSize: 10, fontWeight: 700,
                                              padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
                                              background: item.spec_category
                                                ? (['linear_dimensions','angular_dimensions','circular_radial_dimensions','chamfer_bevel_dimensions','coordinate_dimensions','thread_dimensions','surface_dimensions','taper_slope_dimensions','gdt_feature_control'].includes(item.spec_category) ? '#eff6ff' : '#fdf4ff')
                                                : (item.inspection_type === 'Dimensional' ? '#eff6ff' : '#f0fdf4'),
                                              color: item.spec_category
                                                ? (['linear_dimensions','angular_dimensions','circular_radial_dimensions','chamfer_bevel_dimensions','coordinate_dimensions','thread_dimensions','surface_dimensions','taper_slope_dimensions','gdt_feature_control'].includes(item.spec_category) ? '#1d4ed8' : '#7c3aed')
                                                : (item.inspection_type === 'Dimensional' ? '#1d4ed8' : '#15803d'),
                                            }}>
                                              {item.spec_category ? (CAT_LABELS[item.spec_category] || item.spec_category) : (item.inspection_type || '—')}
                                            </span>
                                            {item.spec_sub_type && <span style={{ fontSize: 10, color: '#64748b' }}>{item.spec_sub_type}</span>}
                                          </div>
                                        </td>
                                        <td style={s.td}>{item.inspection_tool || '—'}</td>
                                        <td style={{ ...s.td, fontSize: 11 }}>{item.gdt?.trim() ? item.gdt : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                          <GdtSection rows={fGdtRows} />
                          {/* Low-confidence rows hidden for reviewers */}
                          <NotesTable items={fNotesItems} />
                        </>
                      )}

                      {/* Per-file action buttons */}
                      {!decided && !alreadyReviewed && !batchAllDone && !actionDone && (
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 18 }}>
                          <button
                            style={{ ...s.rejectBtn, ...(actionLoading ? s.btnDisabled : {}) }}
                            onClick={() => setBatchActionModal({ type: 'reject', drawingId: f.drawing_id })}
                            disabled={actionLoading || expiredView}
                          >
                            Reject This File
                          </button>
                          <button
                            style={{ ...s.approveBtn, ...(actionLoading ? s.btnDisabled : {}) }}
                            onClick={() => setBatchActionModal({ type: 'approve', drawingId: f.drawing_id })}
                            disabled={actionLoading || expiredView}
                          >
                            Approve This File
                          </button>
                        </div>
                      )}

                      {decided && (
                        <div style={{
                          marginTop: 16, padding: '10px 14px', borderRadius: 8,
                          background: dec === 'approved' ? '#F0FDF4' : '#FEF2F2',
                          border: `1px solid ${dec === 'approved' ? '#86EFAC' : '#FCA5A5'}`,
                          fontSize: 13, color: dec === 'approved' ? '#166534' : '#991B1B',
                          display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          <span style={{ fontWeight: 700 }}>{dec === 'approved' ? '✓' : '✕'}</span>
                          This file was {dec}.
                          {batchFileDecisions[String(f.drawing_id)]?.comment && (
                            <span style={{ color: '#5C5450', marginLeft: 4 }}>
                              Comment: "{batchFileDecisions[String(f.drawing_id)].comment}"
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {batchActionModal && (
          <ActionModal
            type={batchActionModal.type}
            loading={actionLoading}
            onConfirm={handleBatchAction}
            onCancel={() => setBatchActionModal(null)}
          />
        )}
      </div>
    );
  }

  /* ── Full report view (single file) ── */
  const alreadyReviewed = reportData?.already_reviewed === true;
  const meta      = reportData?.metadata || {};
  const items     = reportData?.inspection_plan_rows || [];
  const lcItems   = reportData?.low_confidence_dimensions || [];
  const gdtRows   = reportData?.gdt_table_rows || [];
  const notesItems = reportData?.notes_items || [];
  const isTabular = reportData?.scan_type === 'tabular';
  const tabularVariants = reportData?.variants || [];

  return (
    <div style={s.page}>
      {/* Sticky header — outside the scroll container */}
      <header style={s.pageHeader}>
        <div style={s.logoRow}>
          <div style={s.logoIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
            </svg>
          </div>
          <span style={{ ...s.logoText, fontSize: '0.95rem' }}>InSpectly.AI</span>
        </div>
        <div style={s.headerRight}>
          {onBackToDashboard && (
            <button
              onClick={onBackToDashboard}
              style={s.backBtn}
            >
              ← Dashboard
            </button>
          )}
          <span style={s.reviewerChip}>Reviewer View</span>
          {authUser && (
            <span style={s.headerUser}>
              {authUser.username}
            </span>
          )}
        </div>
      </header>

      {/* Scrollable body */}
      <div style={s.content}>

        {/* Action done banner */}
        {actionDone && (
          <div style={s.successBanner}><span style={s.successCheck}>✓</span>{actionDone}</div>
        )}
        {actionError && (
          <div style={s.errorBanner}><span style={s.errorDot}>!</span>{actionError}</div>
        )}

        {/* Already-reviewed decision banner */}
        {alreadyReviewed && !actionDone && (() => {
          const status = reportData?.review_status;
          const isApproved = status === 'approved';
          const reviewerLine = reportData?.reviewer_name
            ? `${reportData.reviewer_name}${reportData.reviewer_email ? ` (${reportData.reviewer_email})` : ''}`
            : reportData?.reviewer_email || 'reviewer';
          return (
            <div style={{
              backgroundColor: isApproved ? '#F0FDF4' : '#FEF2F2',
              border: `1px solid ${isApproved ? '#86EFAC' : '#FCA5A5'}`,
              borderRadius: 12, padding: '14px 20px', marginBottom: 16,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{
                  display: 'inline-block', padding: '3px 14px', borderRadius: 20,
                  fontSize: '0.78rem', fontWeight: 700,
                  backgroundColor: isApproved ? '#D1FAE5' : '#FEE2E2',
                  color: isApproved ? '#065F46' : '#991B1B',
                }}>
                  {isApproved ? '✓ Approved' : '✕ Rejected'}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#5C5450' }}>
                  {isApproved ? 'Approved' : 'Rejected'} by <strong style={{ color: '#1A1410' }}>{reviewerLine}</strong>
                  {reportData?.reviewed_at && (
                    <span style={{ marginLeft: 8, fontSize: '0.78rem', color: '#9C9490' }}>
                      · {toUTC(reportData.reviewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </span>
              </div>
              {reportData?.reviewer_comments && (
                <p style={{ margin: '10px 0 0', fontSize: '0.84rem', color: '#1A1410', fontStyle: 'italic', lineHeight: 1.55 }}>
                  "{reportData.reviewer_comments}"
                </p>
              )}
            </div>
          );
        })()}

        {/* Drawing info */}
        <div style={s.card}>
          <h2 style={s.sectionTitle}>Drawing Information</h2>
          <div style={s.metaGrid}>
            {[
              ['Part No.',     meta.part_no    || '—'],
              ['Part Name',    meta.part_name  || '—'],
              ['Drawing No.',  meta.drawing_no || '—'],
              ['Revision',     meta.rev_no     || '—'],
              ['File Name',    reportData?.file_name || '—'],
              ['CC Symbol',    CC_SYMBOL_LABELS[reportData?.cc_symbol] || reportData?.cc_symbol || 'Spade (♠)'],
              ['Scan Date',    fmtDate(reportData?.upload_timestamp)],
              ['Shared By',    reportData?.uploader_name || '—'],
              ['CC Count',     reportData?.cc_count ?? '—'],
              ['Item Count',   reportData?.item_count ?? '—'],
            ].map(([label, value]) => (
              <div key={label} style={s.metaItem}>
                <span style={s.metaLabel}>{label}</span>
                <span style={s.metaValue}>{String(value)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Images */}
        {(reportData?.input_image_url || (!isTabular && (reportData?.annotated_image_urls?.length || reportData?.annotated_image_url))) && (
          <div style={s.imagesRow}>
            {reportData?.input_image_url && (
              <div style={s.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                  <h2 style={{ ...s.sectionTitle, marginBottom: 0 }}>Original Drawing</h2>
                  {reportData?.input_file_download_url && (
                    <a
                      href={withToken(reportData.input_file_download_url, token)}
                      download
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '6px 14px', borderRadius: 6,
                        background: '#f0f9ff', border: '1px solid #bae6fd',
                        color: '#0369a1', fontWeight: 500, fontSize: 13,
                        textDecoration: 'none', whiteSpace: 'nowrap',
                      }}
                    >
                      ⬇ Download Drawing
                    </a>
                  )}
                </div>
                <iframe
                  title="Original drawing"
                  src={withToken(`/api/image/${reportData.drawing_id}/pdf`, token)}
                  style={{ width: '100%', height: '60vh', border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc' }}
                />
              </div>
            )}
          </div>
        )}

        {/* ── Tabular scan: one accordion per part ── */}
        {isTabular && tabularVariants.length > 0 && (
          <div style={s.card}>
            <h2 style={s.sectionTitle}>
              Inspection Plans — {tabularVariants.length} Part{tabularVariants.length !== 1 ? 's' : ''}
            </h2>
            <TabularPartsAccordion variants={tabularVariants} />
          </div>
        )}

        {/* ── Normal scan: Main plan + GD&T + LC + Notes ── */}
        {!isTabular && (
          <>
            <div style={s.card}>
              <h2 style={s.sectionTitle}>
                Inspection Plan
                <span style={s.planCount}>{items.length} items</span>
              </h2>
              {items.length === 0 ? (
                <p style={{ color: '#9C9490', fontSize: '0.85rem', margin: 0 }}>No inspection items found.</p>
              ) : (
                <div style={s.tableWrap}>
                  <table style={s.planTable}>
                    <thead>
                      <tr>
                        {['#', 'Location', 'CC', 'Specification', 'Nominal', '+Tol', '-Tol', 'UCL', 'LCL', 'Type / Classification', 'Tool', 'GD&T'].map(h => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, idx) => (
                        <tr key={idx} style={s.planRow}>
                          <td style={{ ...s.td, color: '#9C9490' }}>{item.item_no}</td>
                          <td style={s.td}>{item.location_on_drawing || '—'}</td>
                          <td style={s.td}>
                            {normalizeCC(item.critical_characteristic) === 'Yes'
                              ? <span style={s.ccYes}>CC</span>
                              : <span style={s.ccNo}>—</span>}
                          </td>
                          <td style={s.td}>{item.specification || '—'}</td>
                          <td style={s.tdNum}>{item.nominal ?? '—'}</td>
                          <td style={s.tdNum}>{fmtTolPlus(item.tolerance_plus)}</td>
                          <td style={s.tdNum}>{fmtTolMinus(item.tolerance_minus)}</td>
                          <td style={s.tdNum}>{item.ucl ?? '—'}</td>
                          <td style={s.tdNum}>{item.lcl ?? '—'}</td>
                          <td style={{ ...s.td, minWidth: 130 }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              <span style={{
                                display: 'inline-block', fontSize: 10, fontWeight: 700,
                                padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap',
                                background: item.spec_category
                                  ? (['linear_dimensions','angular_dimensions','circular_radial_dimensions','chamfer_bevel_dimensions','coordinate_dimensions','thread_dimensions','surface_dimensions','taper_slope_dimensions','gdt_feature_control'].includes(item.spec_category) ? '#eff6ff' : '#fdf4ff')
                                  : (item.inspection_type === 'Dimensional' ? '#eff6ff' : '#f0fdf4'),
                                color: item.spec_category
                                  ? (['linear_dimensions','angular_dimensions','circular_radial_dimensions','chamfer_bevel_dimensions','coordinate_dimensions','thread_dimensions','surface_dimensions','taper_slope_dimensions','gdt_feature_control'].includes(item.spec_category) ? '#1d4ed8' : '#7c3aed')
                                  : (item.inspection_type === 'Dimensional' ? '#1d4ed8' : '#15803d'),
                              }}>
                                {item.spec_category ? (CAT_LABELS[item.spec_category] || item.spec_category) : (item.inspection_type || '—')}
                              </span>
                              {item.spec_sub_type && (
                                <span style={{ fontSize: 10, color: '#64748b', whiteSpace: 'nowrap' }}>{item.spec_sub_type}</span>
                              )}
                              {item.spec_category && item.inspection_type && (
                                <span style={{ fontSize: 9, color: '#94a3b8', fontStyle: 'italic' }}>{item.inspection_type}</span>
                              )}
                            </div>
                          </td>
                          <td style={s.td}>
                            {item.inspection_tool || '—'}
                            {item.tool_source === 'model' && (
                              <span title={item.tool_rationale || 'Selected by the model'} style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: '#dbeafe', color: '#1d4ed8', marginLeft: 5, textTransform: 'uppercase', letterSpacing: .4, verticalAlign: 'middle' }}>AI</span>
                            )}
                          </td>
                          <td style={{ ...s.td, whiteSpace: 'pre-wrap', maxWidth: 220, fontSize: 11 }}>{item.gdt?.trim() ? item.gdt : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <GdtSection rows={gdtRows} />
            {/* Low-confidence rows hidden for reviewers */}
            <NotesTable items={notesItems} />
          </>
        )}

      </div>

      {expiredView && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 50,
          background: '#FFFBEB', borderBottom: '1.5px solid #FCD34D',
          color: '#92400E', padding: '10px 20px', fontSize: 14, fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>⏳</span>
          <span>This review link has expired — you can still VIEW everything, but approving or
          rejecting is disabled. Ask the uploader for a fresh link to submit a decision.</span>
        </div>
      )}

      {/* Sticky action bar — hidden once reviewed or action done */}
      {!actionDone && !tokenError && !alreadyReviewed && !expiredView && (
        <div style={s.actionBar}>
          <p style={s.actionBarText}>
            Scroll up to review the full inspection plan, then submit your decision below.
          </p>
          <div style={s.actionBtns}>
            <button
              style={{ ...s.approveBtn, ...(actionLoading ? s.btnDisabled : {}) }}
              onClick={() => setActionModal('approve')}
              disabled={actionLoading}
            >
              Approve
            </button>
            <button
              style={{ ...s.rejectBtn, ...(actionLoading ? s.btnDisabled : {}) }}
              onClick={() => setActionModal('reject')}
              disabled={actionLoading}
            >
              Reject
            </button>
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <ActionModal
          type={actionModal}
          loading={actionLoading}
          onConfirm={handleAction}
          onCancel={() => setActionModal(null)}
        />
      )}
    </div>
  );
}

/* ── MessageCard ─────────────────────────────────────────────── */
function MessageCard({ type, title, message }) {
  const colors = {
    error:   { bg: '#FEF2F2', border: '#FCA5A5', icon: '#DC2626', text: '#991B1B' },
    warning: { bg: '#FFFBEB', border: '#FDE68A', icon: '#B45309', text: '#92400E' },
    info:    { bg: '#EFF6FF', border: '#BFDBFE', icon: '#2563EB', text: '#1E3A8A' },
  };
  const c = colors[type] || colors.info;
  return (
    <div style={{ maxWidth: 440, width: '100%' }}>
      <div style={s.logoRow}>
        <div style={s.logoIcon}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
          </svg>
        </div>
        <span style={s.logoText}>InSpectly.AI</span>
      </div>
      <div style={{ backgroundColor: c.bg, border: `1px solid ${c.border}`, borderRadius: 14, padding: '24px', marginTop: 24 }}>
        <h2 style={{ color: c.text, margin: '0 0 8px', fontSize: '1.05rem', fontWeight: 700 }}>{title}</h2>
        <p style={{ color: c.text, margin: 0, fontSize: '0.88rem', lineHeight: 1.5 }}>{message}</p>
      </div>
    </div>
  );
}

/* ── Styles ───────────────────────────────────────────────────── */
const s = {
  /* Layout — sticky header + scrollable body */
  page: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F7F4F0',
    fontFamily: "'Inter','Segoe UI',sans-serif",
  },
  content: {
    flex: 1,
    overflowY: 'auto',   /* the ONLY scrollable area when standalone */
    maxWidth: 1100,
    width: '100%',
    margin: '0 auto',
    padding: '24px 24px 80px',
    boxSizing: 'border-box',
  },

  centerPage: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#F7F4F0', fontFamily: "'Inter','Segoe UI',sans-serif",
    padding: 24, flexDirection: 'column',
  },
  authGateWrap: { minHeight: '100vh', backgroundColor: '#F7F4F0' },
  authGateBanner: {
    backgroundColor: '#FFF7ED', borderBottom: '1px solid #FDBA74',
    padding: '12px 24px', fontSize: '0.88rem', color: '#9A3412',
  },

  /* Header — fixed at top, not in the scroll container */
  pageHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 24px',
    backgroundColor: '#18120E',
    flexShrink: 0,   /* never shrink — content scrolls below */
    zIndex: 10,
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12 },
  reviewerChip: {
    padding: '4px 12px', borderRadius: 20,
    backgroundColor: 'rgba(5,150,105,0.2)', color: '#34D399',
    fontSize: '0.75rem', fontWeight: 600,
    border: '1px solid rgba(5,150,105,0.3)',
  },
  headerUser: {
    color: '#A89E97', fontSize: '0.8rem', fontWeight: 500,
  },
  backBtn: {
    padding: '5px 14px', backgroundColor: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.18)', borderRadius: 7,
    color: '#D4CCC6', fontSize: '0.8rem', fontWeight: 500,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  logoRow:  { display: 'flex', alignItems: 'center', gap: 10 },
  logoIcon: {
    width: 32, height: 32, borderRadius: 8,
    backgroundColor: 'rgba(249,115,22,0.18)',
    border: '1px solid rgba(249,115,22,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#F97316',
  },
  logoText:    { color: '#FFFFFF', fontWeight: 700, fontSize: '1.05rem' },
  loadingText: { color: '#5C5450', fontSize: '0.9rem', marginTop: 16 },

  /* Banners */
  successBanner: {
    display: 'flex', alignItems: 'center', gap: 10,
    backgroundColor: '#F0FDF4', border: '1px solid #86EFAC',
    borderRadius: 10, padding: '12px 16px', color: '#166534',
    fontSize: '0.88rem', fontWeight: 500, marginBottom: 16,
  },
  successCheck: {
    width: 22, height: 22, borderRadius: '50%', backgroundColor: '#16A34A',
    color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, flexShrink: 0,
  },
  errorBanner: {
    display: 'flex', alignItems: 'center', gap: 10,
    backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5',
    borderRadius: 10, padding: '12px 16px', color: '#DC2626',
    fontSize: '0.88rem', marginBottom: 16,
  },
  errorDot: {
    width: 20, height: 20, borderRadius: '50%', backgroundColor: '#DC2626',
    color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
  },

  /* Cards */
  card: { backgroundColor: '#FFFFFF', borderRadius: 14, border: '1px solid #EDE8E1', padding: '20px 24px', marginBottom: 20 },
  sectionTitle: {
    fontSize: '1rem', fontWeight: 700, color: '#1A1410',
    margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 10,
  },
  planCount: { fontSize: '0.78rem', fontWeight: 500, color: '#9C9490', fontStyle: 'normal', marginLeft: 4 },

  /* Meta grid */
  metaGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px 24px' },
  metaItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  metaLabel:{ fontSize: '0.72rem', color: '#9C9490', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
  metaValue:{ fontSize: '0.9rem', color: '#1A1410', fontWeight: 500 },

  /* Images — stacked vertically so reviewer can scroll through each fully */
  imagesRow: { display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 20 },
  drawingImg: {
    width: '100%', borderRadius: 10, border: '1px solid #E8E2DC',
    objectFit: 'contain',
    maxHeight: 600,      /* generous height so drawing details are clear */
    backgroundColor: '#FAFAF9',
  },

  /* Plan table */
  tableWrap: { overflowX: 'auto', marginTop: 8 },
  planTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
  planRow:   { borderBottom: '1px solid #F2EDE8' },
  th:        { padding: '8px 10px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: '#9C9490', backgroundColor: '#FAFAF9', borderBottom: '1px solid #EDE8E1', whiteSpace: 'nowrap' },
  td:        { padding: '9px 10px', color: '#1A1410', verticalAlign: 'middle' },
  tdNum:     { padding: '9px 10px', color: '#2563EB', fontVariantNumeric: 'tabular-nums', verticalAlign: 'middle' },
  ccYes:     { display: 'inline-block', padding: '1px 8px', borderRadius: 20, backgroundColor: '#FFF7ED', color: '#C2410C', fontSize: '0.7rem', fontWeight: 700 },
  ccNo:      { color: '#BDB9B5', fontSize: '0.8rem' },

  /* Action bar — sticky footer, always visible regardless of scroll position */
  actionBar: {
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #EDE8E1',
    boxShadow: '0 -4px 16px rgba(24,18,14,0.06)',
    padding: '14px 24px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 16, flexWrap: 'wrap',
    flexShrink: 0,   /* stays outside scroll — pinned at bottom */
    zIndex: 10,
  },
  actionBarText: { fontSize: '0.84rem', color: '#5C5450', margin: 0 },
  actionBtns:    { display: 'flex', gap: 12 },
  approveBtn: {
    padding: '11px 28px', backgroundColor: '#059669', color: '#FFFFFF',
    border: 'none', borderRadius: 10, fontWeight: 600, fontSize: '0.92rem',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  rejectBtn: {
    padding: '11px 28px', backgroundColor: '#DC2626', color: '#FFFFFF',
    border: 'none', borderRadius: 10, fontWeight: 600, fontSize: '0.92rem',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },

  /* Mini modal */
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(24,18,14,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 24 },
  miniModal: { backgroundColor: '#FFFFFF', borderRadius: 14, padding: '24px', width: '100%', maxWidth: 420, boxShadow: '0 16px 48px rgba(24,18,14,0.2)' },
  miniTitle: { fontSize: '1.05rem', fontWeight: 700, color: '#1A1410', margin: '0 0 8px' },
  miniDesc:  { fontSize: '0.84rem', color: '#5C5450', margin: '0 0 16px', lineHeight: 1.5 },
  textarea:  { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1.5px solid #E8E2DC', borderRadius: 10, fontSize: '0.88rem', color: '#1A1410', fontFamily: 'inherit', resize: 'vertical', outline: 'none' },
  miniActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 },
  cancelBtn:   { padding: '9px 20px', backgroundColor: '#F2EDE8', border: 'none', borderRadius: 9, color: '#5C5450', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.88rem' },
  confirmBtn:  { padding: '9px 20px', border: 'none', borderRadius: 9, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.88rem', color: '#FFFFFF', display: 'flex', alignItems: 'center', gap: 6, minWidth: 90, justifyContent: 'center' },
  confirmApprove: { backgroundColor: '#059669' },
  confirmReject:  { backgroundColor: '#DC2626' },
  spinner: { width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' },
};
